/**
 * Altyazı bindirmesi (overlay).
 *
 * ## Neden `<track>` değil?
 *
 * Tarayıcı `<track>` etiketlerini **yalnız doğrudan oynatmada** uygular.
 * hls.js ise MediaSource API (MSE) kullanır ve MSE yolunda `<track>`
 * **sessizce yoksayılır**.
 *
 * Gerçek sunucuda: 40+ yumuşak altyazılı bir film transcode edildi, kullanıcı
 * iz seçti, liste doğru göründü — ekranda hiçbir şey çıkmadı. Sunucu tarafı
 * sorunsuzdu (`/api/items/:id/subtitle/:index` → `200`, geçerli WebVTT);
 * sorun tamamen bu seçimdeydi.
 *
 * Bu kanca sunucunun zaten ürettiği WebVTT'yi çeker, cue listesine çevirir
 * ve `timeupdate` anına denk gelen satırı döndürür. İki oynatma yolunda da
 * aynı davranış sağlanır.
 */

import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './api.ts';
import { cueAt, parseVtt, type VttCue } from './vtt.ts';

export interface SubtitleOverlay {
  /** Ekranda gösterilecek metin; altyazı yoksa veya eşleşme yoksa `null`. */
  text: string | null;
  /** Seçili iz yoksa `false` (seçici boş kalmalı). */
  enabled: boolean;
  /** Hata veya uyarı mesajı (ör. bitmap biçimi). */
  error: string | null;
  /**
   * Hatanın **türü**. Ekran bu değere göre yanlış çözüm sunmamak için
   * gereklidir: 415 (bitmap) için "Uyumluluk modunu aç" doğru öneridir,
   * 504 (zaman aşımı) için ise tamamen yanlış — kullanıcı dosyayı
   * dönüştürmek zorunda değil, yalnız tekrar denemesi yeterli.
   */
  errorKind: 'burn_required' | 'timeout' | 'other' | null;
  /** `true` iken aynı iz yeniden istenebilir (`retry`). */
  retryable: boolean;
  /** Aynı iz için isteği tekrarlar (yalnız `retryable` iken anlamlı). */
  retry: () => void;
  /**
   * Seçili izin WebVTT'si **hâlâ çekiliyor**.
   *
   * ## Neden bu kadar önemli?
   *
   * Sunucu gömülü altyazıyı dönüştürürken dosyanın **tamamını** okur
   * (Matroska'da altyazı paketleri video kümeleriyle iç içedir); ölçülen
   * süre 2 GB dosyada ~10 sn, 5 GB dosyada ~31 sn, uzak depoda dakikalar.
   *
   * Bu süre boyunca arayüz **hiçbir şey söylemiyordu** — kullanıcı altyazıyı
   * seçmiş oluyor, ekranda hiçbir şey çıkmıyor ve doğal olarak
   * "altyazılar bozuk / görünmüyor" diye yorumluyor. Oysa istek hâlâ
   * karşılanıyordu.
   *
   * `loading` bu sessizliği kılar; `WatchScreen` küçük bir "hazırlanıyor"
   * ipucu gösterir. Başarıdan sonra **önbellek** sayesinde bu bir daha
   * tekrarlanmaz.
   */
  loading: boolean;
}

/**
 * Seçili altyazı izini indirir ve oynatıcının üstüne bindirir.
 *
 * @param activeKey `null` → altyazı kapalı
 */
export function useSubtitleOverlay(
  videoSource: RefObject<HTMLVideoElement | null> | HTMLVideoElement | null,
  itemId: string,
  activeKey: string | null,
): SubtitleOverlay {
  const [cues, setCues] = useState<VttCue[]>([]);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<'burn_required' | 'timeout' | 'other' | null>(null);
  const [loading, setLoading] = useState(false);
  /**
   * Yeniden deneme sayacı.
   *
   * `retry()` yalnız bu sayacı artırır; etiket `[activeIndex, itemId, nonce]`
   * bağımlılığıyla yeniden kurulur. Elle "tekrar çek" mantığı yazmamak,
   * isteğin **iki kez gitmemesini** garanti eder.
   */
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => setNonce((n) => n + 1), []);

  const video = videoSource && 'current' in videoSource ? videoSource.current : videoSource;

  /**
   * Oynatıcı öğesi **geç bağlanır**: `WatchScreen` oynatma hazır değilken
   * `<Preparing>` döndürür, `<video>` DOM'da yoktur. Bu yüzden ilk render'da
   * `video` `null`'dir ve öğe sonradan gelir.
   *
   * `video`'yu bu etiketin **bağımlılığı yapmak** ilk isteği (oynatıcı
   * yokken başlayan) çöpe atardı ve öğe geldiğinde **ikinci** istek
   * başlatırdı. Uzak depoda bu, aynı 5 GB dosyayı **iki kez** okumak demek.
   *
   * Düzeltme: öğeyi bir nesne referansında tutuyoruz; etiket yalnız
   * `[activeIndex, itemId]`'e bağlı. Böylece iz başına **tek** istek atılır.
   */
  const videoRef = useRef<HTMLVideoElement | null>(null);
  videoRef.current = video;

  // İz değişince WebVTT'yi yeniden çek.
  // Eksik `video` bağımlılığı **bilinçli**: oynatıcı geç bağlanıyor ve eklemek,
  // oynatıcı DOM'a girdiğinde aynı dosyanın ikinci kez okunmasına yol açardı
  // (sunucuda tam dosya okuması). Gerekçe: `videoRef` yorumu yukarıda.
  // biome-ignore lint/correctness/useExhaustiveDependencies: nonce = aynı izi tekrar iste
  useEffect(() => {
    if (activeKey === null) {
      setCues([]);
      setText(null);
      setError(null);
      setErrorKind(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let waitingForPrepare = false;
    setCues([]);
    setText(null);
    setError(null);
    setErrorKind(null);
    setLoading(true);

    void api
      .subtitleText(itemId, activeKey)
      .then((raw) => {
        if (cancelled) return;
        const parsed = parseVtt(raw);
        setCues(parsed);
        // İlk kare için hemen deneyelim
        const element = videoRef.current;
        if (element !== null) setText(cueAt(parsed, element.currentTime)?.text ?? null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCues([]);
        setText(null);
        if (err instanceof ApiError && err.status === 415) {
          setErrorKind('burn_required');
          setError(
            'Bu altyazı görüntü tabanlı (PGS/VobSub) ve bu sürümde video üzerine yakılamıyor. Altyazıyı kapatıp altyazısız devam edebilirsiniz.',
          );
        } else if (err instanceof ApiError && err.status === 425) {
          // Ön hazırlama sürüyor: hata gösterme; birkaç saniye sonra tekrar dene.
          waitingForPrepare = true;
          setErrorKind(null);
          setError(null);
          setLoading(true);
          retryTimer = setTimeout(() => {
            if (!cancelled) setNonce((n) => n + 1);
          }, 3_000);
        } else if (err instanceof ApiError && err.status === 504) {
          setErrorKind('timeout');
          setError(
            'Altyazı arka planda hazırlanırken zaman aşımına uğradı. Dosya çok büyük veya uzak bağlantı yavaş. ' +
              'Birkaç dakika sonra tekrar deneyin — hazır olduğunda bir daha okunmayacak.',
          );
        } else {
          setErrorKind('other');
          setError(err instanceof Error ? err.message : 'Altyazı okunamadı.');
        }
      })
      .finally(() => {
        if (!cancelled && !waitingForPrepare) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  }, [activeKey, itemId, nonce]);

  // Oynatıcı konumu değiştikçe aktif satırı güncelle.
  useEffect(() => {
    if (activeKey === null || cues.length === 0 || !video) return;

    const update = (): void => {
      setText(cueAt(cues, video.currentTime)?.text ?? null);
    };

    update();
    video.addEventListener('timeupdate', update);
    video.addEventListener('seeked', update);
    video.addEventListener('seeking', update);
    video.addEventListener('play', update);
    return () => {
      video.removeEventListener('timeupdate', update);
      video.removeEventListener('seeked', update);
      video.removeEventListener('seeking', update);
      video.removeEventListener('play', update);
    };
  }, [activeKey, cues, video]);

  return {
    text,
    enabled: activeKey !== null,
    error,
    errorKind,
    loading,
    retryable: errorKind === 'timeout' || errorKind === 'other',
    retry,
  };
}
